import React, { useCallback, useRef } from 'react'

function UploadIcon() {
  return (
    <svg aria-hidden="true" className="guided-welcome__upload-icon" viewBox="0 0 24 24">
      <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
    </svg>
  )
}

export function UploadProgress({ uploadingFiles }) {
  if (uploadingFiles.length === 0) return null

  return (
    <div className="guided-upload-progress">
      {uploadingFiles.map(file => {
        const numericProgress = file.progress == null ? Number.NaN : Number(file.progress)
        const progress = Number.isFinite(numericProgress)
          ? Math.min(100, Math.max(0, Math.round(numericProgress)))
          : null

        return (
          <div className="guided-upload-progress__item" key={file.tempId}>
            <div className="guided-upload-progress__details">
              <span className="guided-upload-progress__filename">{file.filename}</span>
              <span className="guided-upload-progress__status">
                {progress === null ? 'Uploading' : `Uploading · ${progress}%`}
              </span>
            </div>
            <progress
              aria-label={`Uploading ${file.filename}`}
              className="guided-upload-progress__bar"
              max="100"
              value={progress ?? undefined}
            >
              {progress === null ? 'Uploading' : `${progress}%`}
            </progress>
          </div>
        )
      })}
    </div>
  )
}

export function PreparingArtwork({ images }) {
  const pendingImages = images.filter(image => image.processingStatus === 'pending')
  if (pendingImages.length === 0) return null
  const filenames = pendingImages.map(image => image.originalFilename).filter(Boolean)
  const details = filenames.length === pendingImages.length
    ? filenames.join(', ')
    : `${pendingImages.length} artwork file${pendingImages.length === 1 ? '' : 's'}`

  return (
    <div className="guided-welcome__preparing" role="status">
      <span className="guided-welcome__preparing-spinner" aria-hidden="true" />
      <div>
        <strong>Preparing artwork</strong>
        <p>{details}</p>
      </div>
    </div>
  )
}

export default function GuidedWelcome({
  vendorName,
  customerPortalPath,
  images = [],
  uploads,
  maxImageCount,
  onOpenManualEditor,
}) {
  const { uploadFiles, uploadingFiles, uploadErrors } = uploads
  const fileInputRef = useRef(null)
  const atImageLimit = images.length + uploadingFiles.length >= maxImageCount

  const openFilePicker = useCallback(() => {
    if (!atImageLimit) fileInputRef.current?.click()
  }, [atImageLimit])

  const handleFileSelection = useCallback((event) => {
    const files = event.target.files
    if (files?.length) uploadFiles(files)
    event.target.value = ''
  }, [uploadFiles])

  return (
    <main className="guided-welcome" data-testid="guided-welcome-screen">
      <div className="guided-welcome__header">
        <span className="guided-welcome__brand">{vendorName || 'GSPressHQ'}</span>
        {customerPortalPath && (
          <a className="guided-welcome__customer-link" href={customerPortalPath} target="_top">
            View your gang sheets
          </a>
        )}
      </div>

      <section className="guided-welcome__content" aria-labelledby="guided-welcome-title">
        <div className="guided-welcome__intro">
          <p className="guided-welcome__eyebrow">Gang sheet builder</p>
          <h1 id="guided-welcome-title">Build your gang sheet</h1>
          <p>
            Upload your artwork, choose the sizes and quantities you need, and we’ll arrange it
            automatically.
          </p>
        </div>

        <div
          className={`guided-welcome__dropzone${atImageLimit ? ' guided-welcome__dropzone--disabled' : ''}`}
          data-testid="guided-welcome-dropzone"
        >
          <UploadIcon />
          <h2>{atImageLimit ? 'Artwork limit reached' : 'Drop artwork anywhere'}</h2>
          <p>
            {atImageLimit
              ? `This gang sheet can include up to ${maxImageCount} artwork ${maxImageCount === 1 ? 'file' : 'files'}. Remove a design or finish this gang sheet and start another.`
              : 'Drag files into this window or choose them from your device.'}
          </p>
          <input
            ref={fileInputRef}
            aria-label="Choose artwork files"
            className="guided-welcome__file-input"
            data-testid="guided-welcome-file-input"
            type="file"
            accept=".png,.jpg,.jpeg,.webp"
            multiple
            disabled={atImageLimit}
            onChange={handleFileSelection}
          />
          <button
            className="gs-btn-primary guided-welcome__choose-button"
            type="button"
            disabled={atImageLimit}
            onClick={openFilePicker}
          >
            {atImageLimit ? 'Artwork limit reached' : 'Choose files'}
          </button>
          <p className="guided-welcome__limits">
            PNG, JPG, JPEG, or WebP · 100 MB per file · 2 GB total · up to {maxImageCount} artwork files
          </p>
        </div>

        {uploadErrors.length > 0 && (
          <ul className="guided-welcome__errors" role="alert">
            {uploadErrors.map((error, index) => <li key={`${error}-${index}`}>{error}</li>)}
          </ul>
        )}

        <UploadProgress uploadingFiles={uploadingFiles} />
        <PreparingArtwork images={images} />

        <button
          className="gs-btn-secondary guided-welcome__manual-button"
          type="button"
          onClick={onOpenManualEditor}
        >
          Open manual editor
        </button>
      </section>
    </main>
  )
}
